import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';

import classNames from 'classnames';
import {FieldArray, change, formValueSelector} from 'redux-form';

import {Session} from 'nutshell-core/session';
import type {Market} from 'nutshell-core/types';

import {FormField} from 'shells/form/form-field';

import {LEADS_FORM_KEY} from '../../../entity-form/forms/lead-form-connected';
import {RevenueWidgetOnboardingInfo} from '../../../lead/sidebar/revenue-widget/components/revenue-widget-onboarding-info';
import {ValueEditor} from '../../../lead/sidebar/revenue-widget/components/value-editor';

import {MarketField} from './market-field';
import {ProductSelect} from './product-select';

import styles from './revenue-select-field.module.css';

type Props = {
    fieldKey: string;
    title: string;
    isRequired: boolean;
    validate: (...args: any) => any;
    currentMarket: Market | null | undefined;
};

export function RevenueSelectField(props: Props) {
    const [isEditingManualValue, setIsEditingManualValue] = React.useState<boolean>(false);

    const dispatch = useDispatch();

    const manualValueFieldClassName = classNames(styles['manual-value-field'], {
        [styles['manual-value-field--editing']]: isEditingManualValue,
    });

    const productMaps = useSelector((state) =>
        // @ts-expect-error
        formValueSelector(LEADS_FORM_KEY)(state, 'links.productMaps')
    );
    const manualValueFromForm = useSelector((state) =>
        // @ts-expect-error
        formValueSelector(LEADS_FORM_KEY)(state, 'manualValue')
    );

    const productsValue = React.useMemo(() => {
        if (!productMaps || productMaps.length === 0) {
            return 0;
        }

        // @ts-expect-error
        return productMaps.reduce((acc, productMap) => {
            if (!productMap) {
                return acc;
            }

            return acc + Number(productMap.price.amount) * productMap.quantity;
        }, 0);
    }, [productMaps]);

    const handleSubmitManualValue = React.useCallback(
        (value: number) => {
            setIsEditingManualValue(false);
            dispatch(change(LEADS_FORM_KEY, 'manualValue', value));
        },
        [dispatch]
    );

    const resetToProductValue = React.useCallback(() => {
        dispatch(change(LEADS_FORM_KEY, 'manualValue', null));
    }, [dispatch]);

    const manualValueOrProductsValue = React.useMemo(() => {
        if (typeof manualValueFromForm === 'number') {
            return manualValueFromForm;
        }

        if (typeof productsValue === 'number') {
            return productsValue;
        }

        return 0;
    }, [manualValueFromForm, productsValue]);

    const formatValue = React.useCallback(
        (value: number) => {
            return value.toLocaleString(undefined, {
                style: 'currency',
                currency: props.currentMarket
                    ? props.currentMarket.currency
                    : Session.getInstanceDefaultCurrencyCode(),
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        [props.currentMarket]
    );

    const productValueFormatted = React.useMemo(() => {
        if (typeof productsValue === 'number') {
            return formatValue(productsValue);
        }

        return formatValue(0);
    }, [productsValue, formatValue]);

    return (
        <FormField
            key={props.fieldKey}
            innerId={props.fieldKey}
            tooltipInfo={<RevenueWidgetOnboardingInfo />}
            shouldTooltipFadeIn={true}
            label={'Revenue'}
            isRequired={props.isRequired}
            // @ts-expect-error
            validate={props.validate}
        >
            <div className='pos-relative'>
                {NutClientConfig.hasMultipleMarkets && (
                    <div className={styles.market}>
                        <MarketField />
                    </div>
                )}
                <div className={manualValueFieldClassName}>
                    <ValueEditor
                        value={manualValueOrProductsValue}
                        productValueFormatted={productValueFormatted}
                        onSubmit={handleSubmitManualValue}
                        currencyCode={props.currentMarket && props.currentMarket.currency}
                        isEditing={isEditingManualValue}
                        setIsEditing={setIsEditingManualValue}
                        onReset={resetToProductValue}
                        isInManualValueMode={manualValueFromForm != null}
                        isSmall={true}
                        shouldSubmitOnBlur={true}
                    />
                </div>
                <FieldArray
                    name={props.fieldKey}
                    component={ProductSelect}
                    // @ts-expect-error
                    isRequired={props.isRequired}
                    autoFocus={false}
                    shouldAnd={true}
                    {...props}
                />
            </div>
        </FormField>
    );
}
